import { useContext, useState } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { DownloadContext } from '../../lib/context/DownloadContext';
import { Consts, DownloadEventType } from '../../lib/em/EmitTypes';
import { useEventEmitter } from '../../lib/em/Hooks';
import { EventSections } from '../../lib/events/EventLogger';
import EasyInfo from '../EasyInfo';
import { DownloadRowView } from '../reporting/DownloadRowView';
import DraggableMenu from './DraggableMenu';
import { P2GBox, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';

const pageSize = 5;

const Downloads = ({ label }: { label: string }) => {
  const [detached, setDetached] = useLocalStorage<boolean>('downloadIsOpen', false);
  const [limit, setLimit] = useState(pageSize);
  const { useListener } = useEventEmitter<DownloadEventType>();
  const { track } = useAnalytics();
  useListener(Consts.Downloads, () => (detached ? onUnPinned() : onPinned()), [detached]);

  const dctx = useContext(DownloadContext);

  const remove = (id: string) => {
    dctx.dispatch({ type: 'REMOVE', payload: id });
  };

  const onPinned = () => {
    setDetached(true);
    track({
      type: 'downloads_pinned',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const onUnPinned = () => {
    setDetached(false);
    track({
      type: 'downloads_unpinned',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const onContentShow = () => {
    track({
      type: 'downloads_content_show',
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.MyAccount, EventSections.QuickActions])
      }
    });
  };

  const downloads = dctx.state.slice(0, limit);

  return (
    <DraggableMenu
      control={<P2GLink>{label}</P2GLink>}
      detached={detached}
      onPinned={onPinned}
      onUnPinned={onUnPinned}
      onContentShow={onContentShow}
      palette="primary"
      id="Downloads"
      classNames="btn-downloads"
      title="Downloads / Labels"
      width="350px"
      badgeContent={dctx.state.length}
    >
      {downloads.length > 0 ? (
        <>
          {downloads.map((d) => (
            <P2GBox pt={1} pb={1} key={d.UID}>
              <DownloadRowView download={d} onRemove={() => remove(d.UID)} />
            </P2GBox>
          ))}
          {downloads.length < dctx.state.length && (
            <P2GBox textAlign="center">
              <P2GLink onClick={() => setLimit(limit + pageSize)}>Show More</P2GLink>
            </P2GBox>
          )}
        </>
      ) : (
        <P2GBox bordered p={2}>
          <EasyInfo>
            <P2GText variant="body2">A list of your recent downloaded labels will show here.</P2GText>
          </EasyInfo>
        </P2GBox>
      )}
    </DraggableMenu>
  );
};
export default Downloads;
